import { useEffect } from "react";
import ArticleFooterCTA from "../components/ArticleFooterCTA";

export default function Notion() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      data-testid="article-notion"
      className="px-6 md:px-12 lg:px-24"
    >
      <article className="max-w-3xl mx-auto pt-20 md:pt-32 pb-12 relative">
        {/* Subtle glow behind article header */}
        <div
          aria-hidden="true"
          className="glow-orb orb-drift hidden md:block"
          style={{
            width: "420px",
            height: "420px",
            top: "-80px",
            right: "-180px",
            opacity: 0.35,
          }}
        />
        {/* HEADER */}
        <header className="relative border-b border-subtle pb-12 mb-12">
          <span className="fade-up sticker sticker-tilt-left inline-flex">
            <span
              className="w-1.5 h-1.5 rounded-full pulse-dot"
              style={{ background: "var(--accent)" }}
            />
            Field Note · 01 · Workspace
          </span>
          <h1 className="fade-up-1 mt-5 font-display text-primary text-4xl md:text-6xl font-light tracking-tight leading-[1.04]">
            The Notion Workspace Architecture
          </h1>
          <p className="fade-up-2 mt-6 text-secondary text-lg md:text-xl leading-relaxed max-w-2xl">
            How to design a calm, minimalist Notion dashboard that protects your
            focus, tracks every project, and never asks you to rebuild it on a
            Sunday night.
          </p>
          <div className="fade-up-3 mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.22em] text-tertiary">
            <span>9 min read</span>
            <span>·</span>
            <span>For solo operators</span>
            <span>·</span>
            <span>Updated Feb 2026</span>
          </div>
        </header>

        {/* BODY */}
        <div className="prose-editorial fade-up-4">
          <p>
            Most Notion workspaces fail for the same reason: they were built
            like filing cabinets when they should have been built like
            cockpits. A filing cabinet stores. A cockpit decides. If your
            workspace doesn&rsquo;t tell you what to do next within three
            seconds of opening it, the architecture is wrong — not the tool.
          </p>

          <p>
            This is the system I&rsquo;ve refined across seven solo
            businesses. It runs on four pages. Nothing more.
          </p>

          <blockquote>
            A great workspace removes decisions, not options.
          </blockquote>

          <h2>The Four-Page Rule</h2>
          <p>
            Every Notion setup should collapse into four root pages. Anything
            you can&rsquo;t fit here doesn&rsquo;t belong in your daily
            operating system — it belongs in archive.
          </p>

          <ul>
            <li>
              <strong>Today</strong> — your single source of truth for the next
              24 hours. Three tasks. One outcome. Open links.
            </li>
            <li>
              <strong>Projects</strong> — every commitment you&rsquo;ve made to
              yourself or someone else, viewed as a board, not a list.
            </li>
            <li>
              <strong>Library</strong> — notes, references, swipe files. One
              database. Tagged, never foldered.
            </li>
            <li>
              <strong>Engine</strong> — the recurring systems: weekly review,
              monthly numbers, client cadence, automation log.
            </li>
          </ul>

          <p>
            If a page doesn&rsquo;t serve one of those four functions, it
            doesn&rsquo;t live in the sidebar. It lives inside the Library as a
            row.
          </p>

          <h2>Designing the Today Page</h2>
          <p>
            Your Today page is the most expensive surface in your studio. It
            should look like a quiet airport gate, not a dashboard. Three
            sections, top to bottom:
          </p>

          <ul>
            <li>
              <strong>Anchor block</strong> — one sentence in H1. Today&rsquo;s
              outcome. No emoji, no greeting, no quote.
            </li>
            <li>
              <strong>The Three</strong> — exactly three task rows. Linked to
              projects. Nothing more fits. If something doesn&rsquo;t fit, it
              isn&rsquo;t today.
            </li>
            <li>
              <strong>Quick capture</strong> — a single inline database called{" "}
              <code>Inbox</code> for everything that hits you mid-flow. You
              process it at 5pm. Not before.
            </li>
          </ul>

          <p>
            The point of the Today page is friction. Friction protects you.
            When adding a fourth task feels visually wrong, you&rsquo;ve built
            the right surface.
          </p>

          <h2>The Projects Database</h2>
          <p>
            Use a single database. View it as a board, grouped by status:{" "}
            <code>Active</code>, <code>Queued</code>, <code>Paused</code>,{" "}
            <code>Shipped</code>. Resist sub-databases. Resist nested project
            pages. One row equals one project. Pages live inside the row.
          </p>

          <p>
            Properties to keep: <strong>Status</strong>,{" "}
            <strong>Owner of next move</strong>, <strong>Next move</strong>,{" "}
            <strong>Due</strong>. That&rsquo;s it. Add a fifth and the system
            slowly stops working — not in a week, but in three months, when
            you stop opening it.
          </p>

          <blockquote>
            The properties you add today are the friction you fight in
            November.
          </blockquote>

          <h2>The Library Database</h2>
          <p>
            One database. Every note, every reference, every screenshot. Tagged
            by <strong>topic</strong> and <strong>type</strong>. Never foldered.
            Folders force you to decide where something lives. Tags let you
            find it three ways.
          </p>

          <p>
            Add one calculated property called <strong>Heat</strong> — a
            simple last-opened formula. Anything not opened in 90 days
            auto-archives into a separate view. Your active library should feel
            small. A library you can&rsquo;t see the bottom of is a graveyard.
          </p>

          <h2>The Engine</h2>
          <p>
            This is where most Notion setups quietly die. The Engine page holds
            the meta-work: the weekly review template, the monthly numbers
            log, the client cadence tracker, the automation map. If you are building custom automations to feed this workspace, you need high-speed infrastructure. I host my digital assets on Hostinger (<a href="/go/hostinger" className="text-accent hover:underline">/go/hostinger</a>).
          </p>

          <p>
            Build one toggle for each rhythm. Inside each toggle: the
            checklist, the inputs, the outputs. The first time you sit down to
            do a weekly review, you&rsquo;ll edit the toggle. The second time,
            you&rsquo;ll trust it. The third time, you&rsquo;ll feel calm
            opening Notion on Sunday.
          </p>

          <h2>Aesthetic Decisions That Compound</h2>
          <ul>
            <li>
              <strong>One font for everything</strong>. Default sans. Don&rsquo;t
              toggle serif on certain pages. The workspace should feel like
              one room, not four.
            </li>
            <li>
              <strong>Icons over emoji</strong>. Use the line-icon set
              consistently. Pick six. Stop.
            </li>
            <li>
              <strong>No cover images on operational pages</strong>. Covers belong
              on the Library and on shipped project pages. Active work
              doesn&rsquo;t need decoration.
            </li>
            <li>
              <strong>Toggle, don&rsquo;t scroll</strong>. Long pages drain
              attention. Collapse anything you don&rsquo;t need to see at
              first glance.
            </li>
            <li>
              <strong>White space is a feature</strong>. Empty divider blocks
              and empty H1s let the eye rest. A crowded Notion page is a
              crowded mind.
            </li>
          </ul>

          <h2>The Weekly Reset (15 minutes)</h2>
          <p>
            Every Friday afternoon, run this sequence. It is the only thing
            standing between your system and entropy.
          </p>

          <ul>
            <li>Drag everything from Inbox into Projects or Library. Empty it.</li>
            <li>
              Move shipped projects to <code>Shipped</code>. Pause anything you
              haven&rsquo;t touched in 14 days.
            </li>
            <li>
              Open the Engine. Run the weekly review template. Write three
              sentences about what worked.
            </li>
            <li>
              Close Notion. Don&rsquo;t open it again until Monday.
            </li>
          </ul>

          <h2>What This Buys You</h2>
          <p>
            A workspace that doesn&rsquo;t ask you to rebuild it. A Sunday
            night where Notion doesn&rsquo;t feature. A Monday morning where
            the next move is already obvious. That&rsquo;s the entire promise
            — quiet infrastructure that makes loud output possible.
          </p>

          <p>
            Build the four pages. Hold the line on what gets in. Run the
            Friday reset. Everything else is decoration.
          </p>
        </div>

        <ArticleFooterCTA testId="notion-article" />
      </article>
    </main>
  );
}
